import { Link } from "react-router-dom";

export default function MortgageAffordabilityPost() {
    return (
        <>
            <p>
                Here&apos;s a dirty secret about mortgage pre-approval: the number the bank
                approves you for and the number you can actually <em>afford</em> are almost
                never the same. Banks approve based on gross income and your willingness
                to squeeze. You should borrow based on what leaves room for emergencies,
                retirement, and a reasonable life. This article walks through the math —
                and the honest version of the answer.
            </p>

            <h2>The classic 28/36 rule (and why it still works)</h2>
            <p>
                Two numbers, used for over 50 years by lenders and planners alike:
            </p>
            <ul>
                <li><strong>28% front-end ratio:</strong> your total housing cost — principal, interest, taxes, insurance (PITI) — should stay under 28% of gross monthly income.</li>
                <li><strong>36% back-end ratio:</strong> ALL your debts combined (housing + car + student loans + minimum credit card payments) should stay under 36% of gross monthly income.</li>
            </ul>
            <p>
                At $100,000 gross income, that&apos;s $2,333/month on housing and $3,000/month
                total debt. Try those numbers in our{" "}
                <Link to="/calculator/mortgage">mortgage calculator</Link> to see what home
                price they translate to at today&apos;s rates.
            </p>

            <h2>The &ldquo;gross vs take-home&rdquo; trap</h2>
            <p>
                The 28/36 rule uses <em>gross</em> income because that&apos;s how lenders
                underwrite. But you pay your mortgage with <em>take-home</em>. Depending
                on your state, tax situation, and retirement contributions, take-home can
                be 30–45% smaller than gross. If you&apos;re stretching to 28% of gross,
                you might actually be spending 38–45% of take-home on housing — which
                feels awful and leaves zero room for emergencies.
            </p>
            <p>
                A saner anchor: housing at <strong>25% of take-home pay</strong>. That&apos;s
                the threshold where most people report feeling financially calm instead
                of stressed.
            </p>

            <h2>Worked example: $100K single earner in Texas</h2>
            <ul>
                <li>Gross: $100,000/year → $8,333/mo</li>
                <li>Take-home (no state tax, standard deduction, 10% 401(k)): ~$5,600/mo</li>
                <li>28% of gross: $2,333/mo max housing</li>
                <li>25% of take-home: $1,400/mo comfortable housing</li>
            </ul>
            <p>
                At 6.5% interest, 30-year fixed, 20% down, 2.1% Texas property tax, and
                $1,500/yr insurance: <strong>$2,333/month</strong> translates to roughly
                a <strong>$360,000 home</strong>. <strong>$1,400/month</strong> translates
                to roughly a <strong>$215,000 home</strong>. Same income, $145K difference
                in what the bank says you <em>can</em> borrow vs what a financial planner
                would call comfortable.
            </p>

            <h2>Three costs banks ignore when pre-approving you</h2>
            <ol>
                <li>
                    <strong>Maintenance:</strong> Budget roughly 1% of home value per year.
                    On a $400K home that&apos;s $4,000/year or $333/month that the bank
                    never asks about.
                </li>
                <li>
                    <strong>Increasing property tax:</strong> Most appraisal districts
                    reassess every 1–3 years, and property values have trended up. Plan
                    for your tax escrow to grow 3–5% per year.
                </li>
                <li>
                    <strong>Opportunity cost of the down payment:</strong> A 20% down
                    payment on a $400K house is $80K that would have compounded at ~7%
                    in the stock market. Over 10 years that&apos;s a $157K alternative.
                    Buying a home isn&apos;t free just because you have the cash.
                </li>
            </ol>

            <h2>My simplified rule for first-time buyers</h2>
            <p>
                If you want one number to anchor on: <strong>pick the cheaper of (a) 28%
                of gross income OR (b) 25% of take-home pay</strong>. Then knock another
                10–15% off for margin of safety. That&apos;s your &ldquo;sleep at night&rdquo;
                maximum monthly housing payment. Work backward from there into a home
                price using our calculators.
            </p>
            <p>
                This approach will leave a lot of bank-pre-approved money on the table.
                That&apos;s the point. The goal isn&apos;t to spend the maximum; it&apos;s
                to buy the biggest asset of your life at a price that lets you keep
                contributing to retirement, handle an emergency, and enjoy some of
                your income while you&apos;re young enough to do so.
            </p>

            <h2>Related tools</h2>
            <ul>
                <li><Link to="/calculator/mortgage">Mortgage Calculator</Link> — the core tool</li>
                <li><Link to="/calculator/rent-vs-buy">Rent vs Buy Calculator</Link> — compare owning vs renting over 10+ years</li>
                <li><Link to="/calculator/salary">Salary Calculator</Link> — convert gross to take-home</li>
                <li><Link to="/calculator/debt-payoff">Debt Payoff Calculator</Link> — see how much debt you&apos;re carrying into a mortgage</li>
            </ul>
        </>
    );
}
